/**
 * Code Wave Component Library
 * Reusable React Components with Design System
 * Version: 1.0
 */

import React from 'react';
import Icon, { ICON_MAP } from './Icon';
import FeatureIcon from './FeatureIcon';
import './component-library.css';

// Re-exporting for compatibility if needed elsewhere
export { Icon, FeatureIcon };




/* ==================== BUTTONS ==================== */

/**
 * Primary Button - Main CTAs
 * @param {string} children - Button text
 * @param {function} onClick - Click handler
 * @param {boolean} disabled - Disabled state
 * @param {string} size - 'sm', 'md', 'lg'
 * @param {boolean} fullWidth - Full width button
 */
export const ButtonPrimary = ({
    children,
    onClick,
    disabled = false,
    size = 'md',
    fullWidth = false,
    className = '',
    ...props
}) => {
    return (
        <button
            className={`cw-btn cw-btn-primary cw-btn-${size} ${fullWidth ? 'cw-btn-full' : ''} ${className}`}
            onClick={onClick}
            disabled={disabled}
            {...props}
        >
            {children}
        </button>
    );
};

/**
 * Secondary Button - Supporting actions
 */
export const ButtonSecondary = ({ children, onClick, disabled = false, size = 'md', className = '', ...props }) => {
    return (
        <button
            className={`cw-btn cw-btn-secondary cw-btn-${size} ${className}`}
            onClick={onClick}
            disabled={disabled}
            {...props}
        >
            {children}
        </button>
    );
};

/**
 * Sunshine Button - Celebration/Achievement CTAs
 */
export const ButtonSunshine = ({ children, onClick, disabled = false, size = 'md', className = '', ...props }) => {
    return (
        <button
            className={`cw-btn cw-btn-sunshine cw-btn-${size} ${className}`}
            onClick={onClick}
            disabled={disabled}
            {...props}
        >
            {children}
        </button>
    );
};

/* ==================== CARDS ==================== */

/**
 * Standard Card Component
 * @param {ReactNode} children - Card content
 * @param {boolean} hoverable - Enable hover effect
 * @param {string} className - Additional classes
 */
export const Card = ({ children, hoverable = true, className = '', ...props }) => {
    return (
        <div className={`cw-card ${hoverable ? 'cw-card-hoverable' : ''} ${className}`} {...props}>
            {children}
        </div>
    );
};

/**
 * Lesson Card - For course/lesson display
 * @param {string} title - Lesson title
 * @param {string} description - Short description
 * @param {string} icon - Emoji or icon
 * @param {number} progress - Progress percentage (0-100)
 * @param {function} onClick - Click handler
 */
export const LessonCard = ({
    title,
    description,
    icon = 'lessons',
    progress = 0,
    variant = 'ocean',
    onClick,
    className = '',
    ...props
}) => {
    return (
        <Card hoverable={true} className={`cw-lesson-card ${className}`} onClick={onClick} {...props}>
            <div className="cw-lesson-icon-wrapper">
                <FeatureIcon name={icon} variant={variant} size="md" />
            </div>
            <div className="cw-lesson-content">
                <h3 className="cw-lesson-title">{title}</h3>
                <p className="cw-lesson-description">{description}</p>
            </div>
            {progress > 0 && (
                <div className="cw-lesson-progress">
                    <ProgressBar value={progress} size="sm" />
                    <span className="cw-lesson-progress-text">{progress}% مكتمل</span>
                </div>
            )}
        </Card>
    );
};

/**
 * Achievement Card - For displaying achievements/badges
 */
export const AchievementCard = ({
    title,
    description,
    icon = 'achievement',
    unlocked = false,
    date,
    className = '',
    ...props
}) => {
    return (
        <Card className={`cw-achievement-card ${unlocked ? 'unlocked' : 'locked'} ${className}`} {...props}>
            <FeatureIcon name={icon} variant={unlocked ? 'sunshine' : 'gray'} size="lg" />
            <h4 className="cw-achievement-title">{title}</h4>
            <p className="cw-achievement-description">{description}</p>
            {unlocked && date && (
                <span className="cw-achievement-date">{date}</span>
            )}
        </Card>
    );
};

/* ==================== PROGRESS COMPONENTS ==================== */

/**
 * Progress Bar
 * @param {number} value - Progress value (0-100)
 * @param {string} size - 'sm', 'md', 'lg'
 * @param {boolean} showLabel - Show percentage label
 * @param {boolean} animated - Enable shimmer animation
 */
export const ProgressBar = ({
    value = 0,
    size = 'md',
    showLabel = false,
    animated = true,
    className = '',
    ...props
}) => {
    return (
        <div className={`cw-progress-wrapper ${className}`} {...props}>
            <div className={`cw-progress cw-progress-${size}`}>
                <div
                    className={`cw-progress-fill ${animated ? 'cw-progress-animated' : ''}`}
                    style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
                />
            </div>
            {showLabel && <span className="cw-progress-label">{value}%</span>}
        </div>
    );
};

/**
 * XP Bar with Level Display
 */
export const XPBar = ({ currentXP, nextLevelXP, level }) => {
    const progress = (currentXP / nextLevelXP) * 100;

    return (
        <div className="cw-xp-bar-container">
            <div className="cw-xp-header">
                <div className="cw-level-badge">
                    <span className="cw-level-label">المستوى</span>
                    <span className="cw-level-number">{level}</span>
                </div>
                <span className="cw-xp-text">{currentXP} / {nextLevelXP} XP</span>
            </div>
            <ProgressBar value={progress} size="lg" animated={true} />
        </div>
    );
};

/* ==================== BADGES & TAGS ==================== */

/**
 * Badge Component
 * @param {string} children - Badge text
 * @param {string} variant - 'ocean', 'sky', 'sun', 'success', 'warning', 'error'
 * @param {string} size - 'sm', 'md', 'lg'
 */
export const Badge = ({
    children,
    variant = 'ocean',
    size = 'md',
    icon,
    className = '',
    ...props
}) => {
    return (
        <span className={`cw-badge cw-badge-${variant} cw-badge-${size} ${className}`} {...props}>
            {icon && <span className="cw-badge-icon">{icon}</span>}
            {children}
        </span>
    );
};

/* ==================== INPUT COMPONENTS ==================== */

/**
 * Text Input
 * @param {string} label - Input label
 * @param {string} placeholder - Placeholder text
 * @param {string} value - Input value
 * @param {function} onChange - Change handler
 * @param {string} error - Error message
 * @param {string} type - Input type
 */
export const Input = ({
    label,
    placeholder,
    value,
    onChange,
    error,
    icon,
    type = 'text',
    required = false,
    className = '',
    ...props
}) => {
    return (
        <div className={`cw-input-group ${className}`}>
            {label && (
                <label className="cw-input-label">
                    {label}
                    {required && <span className="cw-required">*</span>}
                </label>
            )}
            <div className={`cw-input-wrapper ${icon ? 'has-icon' : ''}`}>
                {icon && <span className="cw-input-icon">{icon}</span>}
                <input
                    type={type}
                    className={`cw-input ${error ? 'cw-input-error' : ''}`}
                    placeholder={placeholder}
                    value={value}
                    onChange={onChange}
                    {...props}
                />
            </div>
            {error && <span className="cw-input-error-text">{error}</span>}
        </div>
    );
};

/**
 * Textarea Component
 */
export const Textarea = ({
    label,
    placeholder,
    value,
    onChange,
    error,
    rows = 4,
    required = false,
    className = '',
    ...props
}) => {
    return (
        <div className={`cw-input-group ${className}`}>
            {label && (
                <label className="cw-input-label">
                    {label}
                    {required && <span className="cw-required">*</span>}
                </label>
            )}
            <textarea
                className={`cw-input cw-textarea ${error ? 'cw-input-error' : ''}`}
                placeholder={placeholder}
                value={value}
                onChange={onChange}
                rows={rows}
                {...props}
            />
            {error && <span className="cw-input-error-text">{error}</span>}
        </div>
    );
};

/* ==================== STATS & METRICS ==================== */

/**
 * Stat Card - Display single metric
 */
export const StatCard = ({
    title,
    value,
    icon,
    variant = 'ocean',
    trend,
    trendDirection = 'up',
    description,
    className = '',
    ...props
}) => {
    return (
        <Card className={`cw-stat-card ${className}`} hoverable={false} {...props}>
            <div className="cw-stat-header">
                {icon && <FeatureIcon name={icon} variant={variant} size="sm" className="ml-3" />}
                <span className="cw-stat-title">{title}</span>
            </div>
            <div className="cw-stat-value">{value}</div>
            {description && <div className="cw-stat-description">{description}</div>}
            {trend && (
                <div className={`cw-stat-trend cw-stat-trend-${trendDirection}`}>
                    {trendDirection === 'up' ? '↑' : '↓'} {trend}
                </div>
            )}
        </Card>
    );
};

/**
 * Streak Counter
 */
export const StreakCounter = ({ days, icon = 'streak' }) => {
    return (
        <div className="cw-streak-counter">
            <FeatureIcon name={icon} variant="sunshine" size="sm" />
            <div className="cw-streak-content">
                <span className="cw-streak-value">{days} أيام</span>
                <span className="cw-streak-label">سلسلة التعلم اليومية</span>
            </div>
        </div>
    );
};

/* ==================== MODALS & OVERLAYS ==================== */

/**
 * Modal Component
 */
export const Modal = ({
    isOpen,
    onClose,
    title,
    children,
    footer,
    size = 'md',
    ...props
}) => {
    if (!isOpen) return null;

    return (
        <div className="cw-modal-overlay" onClick={onClose} {...props}>
            <div
                className={`cw-modal cw-modal-${size}`}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="cw-modal-header">
                    <h2 className="cw-modal-title">{title}</h2>
                    <button className="cw-modal-close" onClick={onClose}>
                        ✕
                    </button>
                </div>
                <div className="cw-modal-body">
                    {children}
                </div>
                {footer && (
                    <div className="cw-modal-footer">
                        {footer}
                    </div>
                )}
            </div>
        </div>
    );
};

/**
 * Score/Result Modal
 */
export const ScoreModal = ({
    isOpen,
    onClose,
    score,
    total,
    message,
    icon = 'rocket',
    ...props
}) => {
    const percentage = Math.round((score / total) * 100);

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size="sm"
            {...props}
        >
            <div className="cw-score-content">
                <FeatureIcon name={icon} variant="sunshine" size="xl" className="mb-6" />
                <div className="cw-score-circle">
                    <span className="cw-score-percentage">{percentage}%</span>
                </div>
                <h3 className="cw-score-result">{score} / {total}</h3>
                <p className="cw-score-message">{message}</p>
                <ButtonPrimary onClick={onClose} fullWidth>
                    رائع! <Icon name="rocket" size={20} />
                </ButtonPrimary>
            </div>
        </Modal>
    );
};

/* ==================== LOADING STATES ==================== */

/**
 * Loading Spinner
 */
export const Spinner = ({ size = 'md', ...props }) => {
    return (
        <div className={`cw-spinner cw-spinner-${size}`} {...props}>
            <div className="cw-spinner-circle"></div>
        </div>
    );
};

/**
 * Skeleton Loader
 */
export const Skeleton = ({ width = '100%', height = '20px', ...props }) => {
    return (
        <div
            className="cw-skeleton"
            style={{ width, height }}
            {...props}
        />
    );
};

/* ==================== ALERTS & NOTIFICATIONS ==================== */

/**
 * Alert Component
 */
export const Alert = ({
    children,
    variant = 'info',
    icon,
    onClose,
    ...props
}) => {
    const defaultIcons = {
        success: <Icon name="check" size={20} />,
        warning: <Icon name="target" size={20} />,
        error: <Icon name="x" size={20} />,
        info: <Icon name="sparkles" size={20} />
    };

    return (
        <div className={`cw-alert cw-alert-${variant}`} {...props}>
            <span className="cw-alert-icon">{icon || defaultIcons[variant]}</span>
            <div className="cw-alert-content">{children}</div>
            {onClose && (
                <button className="cw-alert-close" onClick={onClose}>✕</button>
            )}
        </div>
    );
};

/* ==================== EXPORT ALL ==================== */

export default {
    // Icons
    Icon,
    FeatureIcon,
    ICON_MAP,

    // Buttons
    ButtonPrimary,
    ButtonSecondary,
    ButtonSunshine,

    // Cards
    Card,
    LessonCard,
    AchievementCard,
    StatCard,

    // Progress
    ProgressBar,
    XPBar,

    // Badges
    Badge,

    // Inputs
    Input,
    Textarea,

    // Stats
    StreakCounter,

    // Modals
    Modal,
    ScoreModal,

    // Loading
    Spinner,
    Skeleton,

    // Alerts
    Alert
};
